<div class="pipeline-loading-shell" *ngIf="layoutLoading; else layoutResolved">
  <div class="pipeline-loading-spinner" role="status" aria-label="Loading Data Builder"></div>
</div>
<ng-template #layoutResolved>
<ng-container *ngIf="pipelineDashboard as dashboard; else legacyDataBuilder">
  <section class="pipeline-dashboard-page">
    <div class="pipeline-shell">
      <aside
        class="pipeline-left-panel pipeline-resizable-window"
        [class.is-pipeline-slate-clearing]="pipelineSlateClearing"
        [style.width.px]="pipelinePanelSizes.left"
      >
        <!-- Existing schema / New schema toggle — HIDDEN (commented out on request).
             `schemaMode` stays at its 'existing' default, so the Schema picker below
             still renders and the "New schema name" input stays hidden. Restore this
             block to bring the two-way choice back; nothing else needs changing.
        <div class="pipeline-schema-mode-toggle" role="group" aria-label="Schema mode">
          <button type="button" [class.is-active]="schemaMode === 'existing'" (click)="setSchemaMode('existing')">Existing schema</button>
          <button type="button" [class.is-active]="schemaMode === 'new'" (click)="setSchemaMode('new')">New schema</button>
        </div>
        -->

        <!-- Schema / Workflow pickers - HIDDEN (commented out on request) so a source
             file can be uploaded without choosing either. openSourceFilePicker() no
             longer gates on a resolved schema (see data-builder.component.ts). Restore
             this block AND that guard together to bring the pickers back.
        <div class="pipeline-schema-workflow-row">
          <ng-select
            *ngIf="schemaMode === 'existing'"
            #schemaSel
            class="ss-select pipeline-schema-workflow-select"
            [class.is-open]="!!schemaSel.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="schemaOptions"
            [clearable]="true"
            [searchable]="true"
            [loading]="schemasLoading"
            placeholder="Schema"
            [title]="schemasErrorMessage || null"
            [(ngModel)]="selectedSchema"
            (ngModelChange)="onSchemaSelectionChange($event)"
          ></ng-select>

          <input
            *ngIf="schemaMode === 'new'"
            type="text"
            class="pipeline-new-schema-input"
            placeholder="New schema name"
            [(ngModel)]="newSchemaName"
            (ngModelChange)="onNewSchemaNameChange()"
          />

          <ng-select
            #workflowSel
            class="ss-select pipeline-schema-workflow-select"
            [class.is-open]="!!workflowSel.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="workflowOptions"
            [clearable]="true"
            [searchable]="true"
            [loading]="workflowsLoading"
            placeholder="Workflow"
            [title]="workflowsErrorMessage || null"
            [(ngModel)]="selectedWorkflow"
            (ngModelChange)="onWorkflowSelectionChange($event)"
          ></ng-select>
        </div>
        <small class="control-warning pipeline-schema-mode-warning" *ngIf="schemaModeErrorMessage">{{ schemaModeErrorMessage }}</small>
        -->

        <div class="pipeline-panel-heading">
          <strong>{{ dashboard.sourcesPanel?.title }} ({{ pipelineSourceFiles.length }})</strong>
          <input
            #pipelineSourceFileInput
            type="file"
            multiple
            [attr.accept]="acceptedFileTypes"
            (change)="onFileSelected($event, true)"
            hidden
          />
          <button type="button" [disabled]="schemaCreating" (click)="openSourceFilePicker(pipelineSourceFileInput)">{{ schemaCreating ? 'Creating schema…' : '+ ' + dashboard.sourcesPanel?.addActionLabel }}</button>
        </div>

        <ng-container *ngIf="workbookFile && workbookFromPipeline">
          <ng-container *ngTemplateOutlet="workbookPanel"></ng-container>
        </ng-container>

        <div
          class="pipeline-source-pill-list"
          *ngIf="pipelineSourceFiles.length"
          [class.has-open-menu]="!!openPipelineSourceMenuId"
        >
          <ng-container *ngFor="let file of pipelineSourceFiles; trackBy: trackPipelineSourceFile">
            <div
              class="pipeline-source-card"
              [class.is-selected]="selectedPipelineSourceName === file.name"
              [class.has-open-menu]="openPipelineSourceMenuId === pipelineSourceMenuKey(file)"
              tabindex="0"
              role="button"
              (click)="selectPipelineSourceFile(file)"
              (keydown.enter)="selectPipelineSourceFile(file)"
              (keydown.space)="$event.preventDefault(); selectPipelineSourceFile(file)"
            >
              <div class="pipeline-excel-icon">X</div>
              <div class="pipeline-source-card-copy" [title]="file.name">
                <strong>{{ file.name }}</strong>
              </div>
              <button
                type="button"
                class="pipeline-menu-trigger"
                aria-label="Source actions"
                aria-haspopup="menu"
                [attr.data-source-menu-trigger-key]="pipelineSourceMenuKey(file)"
                [attr.aria-expanded]="openPipelineSourceMenuId === pipelineSourceMenuKey(file)"
                (pointerdown)="onPipelineSourceMenuPointerDown($event, file)"
                (click)="suppressPipelineSourceMenuClick($event)"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </div>
            <div
              class="pipeline-source-delete-popup"
              *ngIf="openPipelineSourceMenuId === pipelineSourceMenuKey(file)"
              role="menu"
              data-pipeline-source-delete-menu="true"
              [attr.data-source-menu-key]="pipelineSourceMenuKey(file)"
              (pointerdown)="$event.stopPropagation()"
              (click)="$event.stopPropagation()"
            >
              <button
                type="button"
                class="pipeline-delete-action"
                role="menuitem"
                (pointerdown)="onPipelineSourceDeletePointerDown($event, file)"
                (click)="suppressPipelineSourceMenuClick($event)"
              >
                <span class="pipeline-delete-action-icon">
                  <mat-icon>delete_outline</mat-icon>
                </span>
                <span class="pipeline-delete-action-copy">
                  <strong>Delete file</strong>
                  <small>Remove from pipeline</small>
                </span>
              </button>
            </div>
          </ng-container>
        </div>

        <div class="pipeline-source-loading" *ngIf="fileUploadLoading">
          <span class="pipeline-spinner" aria-hidden="true"></span>
          <span>{{ fileUploadStatusText }}</span>
        </div>

        <div class="pipeline-source-empty" *ngIf="!fileUploadLoading && pipelineSourceFiles.length === 0">
          No source files added
        </div>

        <div class="pipeline-add-step">
          <h2>Add Step</h2>
          <p>Drag a step onto the canvas or click to add next step.</p>
          <button
            type="button"
            class="pipeline-step-card"
            *ngFor="let item of dashboard.sourcesPanel?.stepLibrary"
            [style.--step-accent]="item.accent"
            [class.is-disabled]="!pipelineStepEnabled(item.id)"
            [disabled]="!pipelineStepEnabled(item.id)"
            [attr.draggable]="pipelineStepEnabled(item.id) ? 'true' : null"
            [title]="pipelineStepDisabledReason(item.id)"
            (dragstart)="onPipelineLibraryDragStart($event, item)"
            (click)="onPipelineStepSelected(item)"
          >
            <span class="pipeline-step-card-icon"><mat-icon>{{ item.icon }}</mat-icon></span>
            <span>
              <strong>{{ item.label }}</strong>
              <small>{{ item.description }}</small>
            </span>
          </button>
        </div>

        <section class="pipeline-datasets" *ngIf="pipelineSavedDatasets.length">
          <div class="pipeline-datasets-heading">
            <h2>Datasets</h2>
            <span>{{ pipelineSavedDatasets.length }}</span>
          </div>
          <div class="pipeline-dataset-pill-list">
            <button
              type="button"
              class="pipeline-dataset-pill"
              *ngFor="let dataset of pipelineSavedDatasets"
              [class.is-selected]="selectedPipelineDatasetIds.includes(dataset.id)"
              [title]="dataset.name"
              (click)="selectPipelineDataset(dataset)"
            >
              <span class="pipeline-dataset-icon"><mat-icon>dataset</mat-icon></span>
              <span class="pipeline-dataset-copy">
                <strong>{{ dataset.name }}</strong>
                <small>Derived dataset</small>
              </span>
            </button>
          </div>
        </section>
        <div class="pipeline-resize-handle pipeline-resize-handle-right" (pointerdown)="startPipelinePanelResize($event, 'left')"></div>
      </aside>

      <main class="pipeline-main">
        <section class="pipeline-canvas" [class.is-maximized]="pipelineCanvasMaximized">
          <div class="pipeline-canvas-header">
            <div>
              <h2>{{ dashboard.designer?.title }} <span>i</span></h2>
              <p>{{ dashboard.designer?.subtitle }}</p>
            </div>
            <div class="pipeline-canvas-tools">
              <button
                type="button"
                class="pipeline-icon-btn"
                (click)="togglePipelineCanvasMaximize()"
                [title]="pipelineCanvasMaximized ? 'Exit fullscreen' : 'Maximize canvas'"
              >
                <mat-icon>{{ pipelineCanvasMaximized ? 'close_fullscreen' : 'fullscreen' }}</mat-icon>
              </button>
            </div>
          </div>

          <div
            class="pipeline-flow"
            [class.is-drop-active]="pipelineCanvasDragActive"
            (dragover)="onPipelineDragOver($event)"
            (dragleave)="onPipelineDragLeave()"
            (drop)="onPipelineCanvasDrop($event)"
          >
            <div class="pipeline-grid-hint">Drop steps anywhere on the grid</div>
            <div
              class="pipeline-canvas-stage"
              [style.width.px]="pipelineCanvasBounds.width"
              [style.height.px]="pipelineCanvasBounds.height"
              [style.transform]="'scale(' + pipelineCanvasScale + ')'"
            >
              <svg class="pipeline-connection-layer" aria-hidden="true">
                <defs>
                  <marker id="pipeline-arrowhead" markerWidth="10" markerHeight="8" refX="9" refY="4" orient="auto">
                    <path d="M 0 0 L 10 4 L 0 8 z"></path>
                  </marker>
                </defs>
                <line
                  *ngFor="let connection of pipelineConnections"
                  [attr.x1]="pipelineConnectionLine(connection).x1"
                  [attr.y1]="pipelineConnectionLine(connection).y1"
                  [attr.x2]="pipelineConnectionLine(connection).x2"
                  [attr.y2]="pipelineConnectionLine(connection).y2"
                  marker-end="url(#pipeline-arrowhead)"
                ></line>
                <line
                  *ngIf="pipelinePreviewConnectionLine() as previewLine"
                  class="pipeline-preview-connection"
                  [attr.x1]="previewLine.x1"
                  [attr.y1]="previewLine.y1"
                  [attr.x2]="previewLine.x2"
                  [attr.y2]="previewLine.y2"
                  marker-end="url(#pipeline-arrowhead)"
                ></line>
              </svg>
              <article
                class="pipeline-node"
                *ngFor="let node of pipelineCanvasNodes; let nodeIndex = index; trackBy: trackPipelineNode"
                [class.is-selected]="node.id === selectedPipelineNodeId"
                [class.is-drag-over]="node.id === dragOverPipelineNodeId"
                [class.is-live-dragging]="node.id === draggedPipelineNodeId"
                [class.has-open-menu]="openPipelineNodeMenuId === node.id"
                [class.is-source-node]="node.type === 'source'"
                [attr.data-node-id]="node.id"
                [style.--node-accent]="node.accent"
                [style.left.px]="pipelineNodeX(node)"
                [style.top.px]="pipelineNodeY(node)"
                draggable="true"
                (dragstart)="onPipelineNodeDragStart($event, node)"
                (dragover)="onPipelineDragOver($event, node.id)"
                (dragleave)="onPipelineDragLeave(node.id)"
                (dragend)="onPipelineDragEnd()"
                (click)="selectPipelineNode($event, node)"
              >
                <button
                  type="button"
                  class="pipeline-port pipeline-port-in"
                  [class.is-connecting]="pipelinePortIsConnecting(node, 'in')"
                  [attr.data-node-id]="node.id"
                  data-side="in"
                  (pointerdown)="handlePipelinePortPointerDown($event, node, 'in')"
                  (pointerup)="handlePipelinePortPointerUp($event)"
                  (click)="handlePipelinePortClick($event, node, 'in')"
                  title="Connect input"
                ></button>
                <mat-icon>{{ pipelineNodeIcon(node.type) }}</mat-icon>
                <strong>{{ node.label }}</strong>
                <span>{{ node.meta }}</span>
                <small *ngIf="node.summary">{{ node.summary }}</small>
                <span class="pipeline-node-check"><mat-icon>check</mat-icon></span>
                <button
                  type="button"
                  class="pipeline-menu-trigger pipeline-node-menu-trigger"
                  aria-label="Node actions"
                  [attr.aria-expanded]="openPipelineNodeMenuId === node.id"
                  (pointerdown)="$event.stopPropagation()"
                  (click)="togglePipelineNodeMenu($event, node)"
                >
                  <mat-icon>more_vert</mat-icon>
                </button>
                <div
                  class="pipeline-context-menu pipeline-node-context-menu"
                  *ngIf="openPipelineNodeMenuId === node.id"
                  (pointerdown)="$event.stopPropagation()"
                  (click)="$event.stopPropagation()"
                >
                  <button type="button" class="pipeline-delete-action" (click)="deletePipelineNode($event, node)">
                    <mat-icon>delete_outline</mat-icon>
                    Delete
                  </button>
                </div>
                <button
                  type="button"
                  class="pipeline-port pipeline-port-out"
                  [class.is-connecting]="pipelinePortIsConnecting(node, 'out')"
                  [attr.data-node-id]="node.id"
                  data-side="out"
                  (pointerdown)="handlePipelinePortPointerDown($event, node, 'out')"
                  (pointerup)="handlePipelinePortPointerUp($event)"
                  (click)="handlePipelinePortClick($event, node, 'out')"
                  title="Connect output"
                ></button>
              </article>
            </div>
          </div>

        </section>

        <section
          class="pipeline-preview-panel pipeline-resizable-window"
          [style.height.px]="pipelinePanelSizes.preview"
        >
          <div class="pipeline-resize-handle pipeline-resize-handle-top" (pointerdown)="startPipelinePanelResize($event, 'preview')"></div>
          <div class="pipeline-preview-tabs">
            <button
              type="button"
              *ngFor="let tab of dashboard.bottomPanel?.tabs"
              [class.is-active]="tab.id === dashboard.bottomPanel?.activeTab"
            >
              {{ tab.label }}
            </button>
            <strong>Rows: {{ pipelinePreviewTotalRows | number }}</strong>
            <button
              type="button"
              class="pipeline-preview-save"
              [class.is-saving-reset]="pipelineSlateClearing"
              [class.is-busy]="savingProgressText"
              [disabled]="!canSavePipelineDataset || !!savingProgressText"
              (click)="savePipelineDatasetFromPreview()"
            >
              <span class="pipeline-spinner" *ngIf="savingProgressText" aria-hidden="true"></span>
              <mat-icon *ngIf="!savingProgressText">save</mat-icon>
              {{ savingProgressText ? saveButtonBusyLabel : 'Save Dataset' }}
            </button>
            <small class="pipeline-save-estimate" *ngIf="!savingProgressText && saveEstimateHint">
              <mat-icon>schedule</mat-icon>{{ saveEstimateHint }}
            </small>
            <span class="pipeline-save-progress" *ngIf="savingProgressText">
              <small><mat-icon>hourglass_top</mat-icon>{{ savingProgressText }}</small>
              <span class="pipeline-save-progress-track">
                <span class="pipeline-save-progress-fill" [style.width.%]="savingProgressPercent"></span>
              </span>
            </span>
            <button type="button" class="pipeline-export"><mat-icon>download</mat-icon>{{ dashboard.bottomPanel?.exportActionLabel }}<mat-icon>chevron_right</mat-icon></button>
          </div>
          <h3>{{ pipelinePreviewTitle }}</h3>
          <div class="pipeline-table-wrap" [class.is-busy]="pipelinePreviewBusyText">
            <!-- Overlay, not just an empty-state row: a re-translation still has
                 the previous rows on screen, so the row variant never shows. -->
            <div class="pipeline-table-overlay" *ngIf="pipelinePreviewBusyText">
              <span class="pipeline-spinner" aria-hidden="true"></span>
              <span>{{ pipelinePreviewBusyText }}</span>
            </div>
            <table>
              <thead>
                <tr>
                  <th *ngFor="let column of pipelinePreviewColumns">{{ column }}</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let row of pipelinePreviewRows">
                  <td *ngFor="let column of pipelinePreviewColumns">{{ pipelinePreviewCell(row[column]) }}</td>
                </tr>
                <tr *ngIf="pipelinePreviewRows.length === 0 && pipelinePreviewBusyText">
                  <td class="pipeline-preview-empty" [attr.colspan]="pipelinePreviewColumns.length || 1">
                    <span class="pipeline-preview-busy">
                      <span class="pipeline-spinner" aria-hidden="true"></span>
                      {{ pipelinePreviewBusyText }}
                    </span>
                  </td>
                </tr>
                <tr *ngIf="pipelinePreviewRows.length === 0 && !pipelinePreviewBusyText">
                  <td class="pipeline-preview-empty" [attr.colspan]="pipelinePreviewColumns.length || 1">
                    Analyze and preview a Translation configuration to display data.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </main>

      <aside
        *ngIf="pipelineDetailsNode && pipelineDetailsNode.type !== 'translation' && pipelineDetailsNode.type !== 'source'"
        class="pipeline-right-panel pipeline-resizable-window"
        [style.width.px]="pipelinePanelSizes.right"
      >
        <div class="pipeline-resize-handle pipeline-resize-handle-left" (pointerdown)="startPipelinePanelResize($event, 'right')"></div>
        <div class="pipeline-detail-tabs">
          <button
            type="button"
            *ngFor="let tab of dashboard.detailsPanel?.tabs"
            [class.is-active]="activePipelineDetailsTab === tab.id"
            (click)="setPipelineDetailsTab(tab.id)"
          >
            {{ tab.id === 'applied-steps' ? 'Applied Steps (' + visiblePipelineAppliedSteps.length + ')' : tab.label }}
          </button>
        </div>

        <ng-container *ngIf="activePipelineDetailsTab === 'node-details' && pipelineDetailsNode as selectedNode">
          <ng-container [ngSwitch]="selectedNode.type">
            <section *ngSwitchCase="'transformation'" class="transformer-operations">
              <button type="button" class="transformer-title" (click)="transformerExpanded = !transformerExpanded">
                <strong>Transformer Operations</strong>
                <mat-icon>{{ transformerExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>

              <div class="transformer-body" *ngIf="transformerExpanded">
                <section class="transformer-section">
                  <h3>1. Select Files / Sources</h3>
                  <div class="transformer-radio-row">
                    <label><input type="radio" name="transformer-file-mode" [checked]="transformerFileMode === 'single'" (change)="setTransformerFileMode('single')" /> Single File</label>
                    <label><input type="radio" name="transformer-file-mode" [checked]="transformerFileMode === 'multiple'" (change)="setTransformerFileMode('multiple')" /> Multiple Files</label>
                  </div>
                  <div class="transformer-picker">
                    <div class="transformer-chips">
                      <span *ngFor="let fileName of transformerSelectedFiles">
                        {{ fileName }}
                        <button type="button" (click)="removeTransformerFile(fileName)" [attr.aria-label]="'Remove ' + fileName"><mat-icon>close</mat-icon></button>
                      </span>
                    </div>
                    <select
                      class="transformer-add-file"
                      aria-label="Add saved dataset"
                      (change)="addTransformerFile($event)"
                    >
                      <option value="">+ Add saved dataset{{ transformerFileMode === 'multiple' ? 's' : '' }}...</option>
                      <option
                        *ngFor="let fileName of transformerAvailableFiles"
                        [value]="fileName"
                        [disabled]="transformerSelectedFiles.includes(fileName)"
                      >
                        {{ fileName }}
                      </option>
                    </select>
                  </div>
                </section>

                <section class="transformer-section">
                  <h3>2. Select Map To Column</h3>
                  <span class="transformer-field-label">Target Metadata mapTo</span>
                  <div class="transformer-radio-row">
                    <label><input type="radio" name="transformer-output-mode" value="overwrite" [(ngModel)]="transformerOutputMode" /> Existing Column</label>
                    <label><input type="radio" name="transformer-output-mode" value="create" [(ngModel)]="transformerOutputMode" /> New Column</label>
                  </div>
                  <label class="transformer-field" *ngIf="transformerOutputMode === 'overwrite'; else newTransformerMapTo">
                    <span>Map To</span>
                    <select [(ngModel)]="transformerMapToColumn" aria-label="Select map to column">
                      <option value="">Select mapTo column</option>
                      <option *ngFor="let columnName of transformerAvailableColumns" [value]="columnName">{{ columnName }}</option>
                    </select>
                  </label>
                  <ng-template #newTransformerMapTo>
                    <label class="transformer-field">
                      <span>New mapTo Column</span>
                      <input type="text" placeholder="For example: total_price" [(ngModel)]="transformerMapToColumn" />
                    </label>
                  </ng-template>
                </section>

                <section class="transformer-section">
                  <h3>3. Select Operation Columns</h3>
                  <div class="transformer-radio-row">
                    <label><input type="radio" name="transformer-column-mode" [checked]="transformerColumnMode === 'single'" (change)="setTransformerColumnMode('single')" /> Single Column</label>
                    <label
                      [class.is-disabled]="!transformerRuleSupportsMultipleColumns"
                      [title]="transformerRuleSupportsMultipleColumns ? '' : 'The selected rule accepts one column'"
                    >
                      <input
                        type="radio"
                        name="transformer-column-mode"
                        [checked]="transformerColumnMode === 'multiple'"
                        [disabled]="!transformerRuleSupportsMultipleColumns"
                        (change)="setTransformerColumnMode('multiple')"
                      />
                      Multiple Columns
                    </label>
                  </div>
                  <div class="transformer-selection-box">
                    <div class="transformer-selection-heading">
                      <strong>Operation Columns ({{ transformerSelectedColumns.length }})</strong>
                      <button type="button" (click)="transformerSelectedColumns = []">Clear All</button>
                    </div>
                    <div class="transformer-chips">
                      <span *ngFor="let columnName of transformerSelectedColumns">
                        {{ columnName }}
                        <button type="button" (click)="removeTransformerColumn(columnName)" [attr.aria-label]="'Remove ' + columnName"><mat-icon>close</mat-icon></button>
                      </span>
                    </div>
                    <select aria-label="Add column" (change)="addTransformerColumn($event)">
                      <option value="">+ Add Column</option>
                      <option *ngFor="let columnName of transformerAvailableColumns" [value]="columnName" [disabled]="transformerSelectedColumns.includes(columnName)">{{ columnName }}</option>
                    </select>
                  </div>
                </section>

                <section class="transformer-section">
                  <h3>4. Select Rule Type</h3>
                  <span class="transformer-field-label">Select Rule</span>
                  <label class="transformer-search">
                    <mat-icon>search</mat-icon>
                    <select
                      aria-label="Search rule type"
                      [ngModel]="transformerRuleType"
                      (ngModelChange)="selectTransformerRule($event)"
                    >
                      <option value="" disabled>Search rule type</option>
                      <option *ngFor="let rule of transformerRuleTypes" [value]="rule.id">
                        {{ rule.label }}
                      </option>
                    </select>
                  </label>
                </section>

                <section class="transformer-section">
                  <h3>5. Rule Options</h3>
                  <article class="transformer-rule-overview" *ngIf="selectedTransformerRule as selectedRule">
                    <span class="transformer-rule-overview-icon">
                      <mat-icon>{{ selectedRule.icon }}</mat-icon>
                    </span>
                    <span class="transformer-rule-overview-copy">
                      <strong>{{ selectedRule.label }}</strong>
                      <small>{{ selectedRule.description }}</small>
                    </span>
                    <span class="transformer-rule-overview-count">
                      {{ transformerSelectedColumns.length }} input{{ transformerSelectedColumns.length === 1 ? '' : 's' }}
                    </span>
                  </article>
                  <ng-container [ngSwitch]="transformerRuleType">
                    <label *ngSwitchCase="'concatenate'" class="transformer-field">
                      <span>Operator / Delimiter</span>
                      <input type="text" placeholder="Space, comma, hyphen..." [(ngModel)]="transformerDelimiter" />
                    </label>
                    <div *ngSwitchCase="'substring'" class="transformer-inline-fields">
                      <label class="transformer-field"><span>Start Index</span><input type="number" min="0" [(ngModel)]="transformerRangeStart" /></label>
                      <label class="transformer-field"><span>End Index</span><input type="number" min="0" [(ngModel)]="transformerRangeEnd" /></label>
                    </div>
                    <div *ngSwitchCase="'validate_range'" class="transformer-option-grid">
                      <label class="transformer-field"><span>Minimum</span><input type="number" [(ngModel)]="transformerRangeMin" /></label>
                      <label class="transformer-field"><span>Maximum</span><input type="number" [(ngModel)]="transformerRangeMax" /></label>
                      <label class="transformer-field"><span>Default Value</span><input type="text" [(ngModel)]="transformerDefaultValue" /></label>
                      <label class="transformer-field"><span>Decimal Precision</span><input type="number" min="0" max="20" [(ngModel)]="transformerDecimalPrecision" /></label>
                    </div>
                    <label *ngSwitchCase="'trim'" class="transformer-field">
                      <span>Default Value When Empty</span>
                      <input type="text" [(ngModel)]="transformerDefaultValue" />
                    </label>
                    <div *ngSwitchCase="'regex_remove'" class="transformer-inline-fields">
                      <label class="transformer-field"><span>Regex</span><input type="text" [(ngModel)]="transformerRegex" /></label>
                      <label class="transformer-field"><span>Replacement</span><input type="text" [(ngModel)]="transformerReplacement" /></label>
                    </div>
                    <label *ngSwitchCase="'to_array'" class="transformer-field">
                      <span>Delimiter</span>
                      <input type="text" placeholder="Default: comma" [(ngModel)]="transformerArrayDelimiter" />
                    </label>
                    <label *ngSwitchCase="'sum'" class="transformer-field">
                      <span>Fixed Numeric Values (comma separated, optional)</span>
                      <input type="text" placeholder="For example: 5.00, 0.18" [(ngModel)]="transformerLiteralValues" />
                    </label>
                    <label *ngSwitchCase="'difference'" class="transformer-field">
                      <span>Fixed Numeric Values (comma separated, optional)</span>
                      <input type="text" placeholder="For example: 20" [(ngModel)]="transformerLiteralValues" />
                    </label>
                    <label *ngSwitchCase="'multiply'" class="transformer-field">
                      <span>Fixed Numeric Values (comma separated, optional)</span>
                      <input type="text" placeholder="For example: 0.18" [(ngModel)]="transformerLiteralValues" />
                    </label>
                    <label *ngSwitchCase="'convert_to_json'" class="transformer-field">
                      <span>Aliases (optional, comma separated)</span>
                      <input type="text" placeholder="id: Customer ID, name: Customer Name" [(ngModel)]="transformerJsonAliases" />
                    </label>
                    <div *ngSwitchCase="'uppercase'" class="transformer-ready-state">
                      <mat-icon>check_circle</mat-icon>
                      <span>
                        <strong>Ready to convert</strong>
                        <small>Select one source column and an output column. No additional parameters are required.</small>
                      </span>
                    </div>
                    <div *ngSwitchCase="'auto_generate'" class="transformer-ready-state">
                      <mat-icon>fingerprint</mat-icon>
                      <span>
                        <strong>Ready to generate</strong>
                        <small>The selected source values will produce a stable generated identifier.</small>
                      </span>
                    </div>
                    <p *ngSwitchDefault class="transformer-option-note">No additional parameters are required for this rule.</p>
                  </ng-container>
                </section>

                <section class="transformer-section transformer-metadata-preview" *ngIf="transformerOperationPreview as operationPreview">
                  <h3>Target Metadata Preview</h3>
                  <div class="transformer-metadata-grid">
                    <span>Attributes</span>
                    <strong>{{ transformerSelectedColumns.join(', ') }}</strong>
                    <span>Operation</span>
                    <strong>{{ operationPreview['operation'] }}</strong>
                    <span>Map To</span>
                    <strong>{{ transformerMapToPreview }}</strong>
                  </div>
                </section>

                <p class="transformer-status" *ngIf="transformerApplyMessage">{{ transformerApplyMessage }}</p>
                <div class="transformer-actions is-two-actions">
                  <button type="button" class="transformer-reset" (click)="resetTransformerOperations()">Reset</button>
                  <button type="button" class="transformer-apply" (click)="finalizeTransformerChanges()">Finalize Changes</button>
                </div>
              </div>
            </section>

            <section *ngSwitchCase="'relationship'" class="transformer-operations">
              <button type="button" class="transformer-title" (click)="transformerExpanded = !transformerExpanded">
                <strong>{{ relationshipPanelTitle }}</strong>
                <mat-icon>{{ transformerExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>

              <div class="transformer-body" *ngIf="transformerExpanded">
                <section class="transformer-section">
                  <h3>1. Select Files / Sources</h3>
                  <div class="transformer-picker">
                    <ng-select
                      class="transformer-multi-select transformer-add-file"
                      [items]="transformerRelationshipSourceOptions"
                      [multiple]="true"
                      [closeOnSelect]="false"
                      [searchable]="true"
                      appendTo="body"
                      [ngModel]="transformerSelectedFiles"
                      (ngModelChange)="onTransformerSelectedFilesChange($event)"
                      (open)="!isRelationshipJoinOperation && loadTransformerLookupEntities(true, false)"
                      [loading]="transformerLookupEntitiesLoading"
                      dropdownPanelClass="transformer-dropdown-panel"
                      placeholder="+ Add source..."
                      aria-label="Add relationship source"
                    >
                    </ng-select>
                  </div>
                </section>

                <section class="transformer-section">
                  <h3>2. {{ isRelationshipJoinOperation ? 'Set Output Entity' : 'Set Output' }}</h3>
                  <label class="transformer-field" *ngIf="!isRelationshipJoinOperation">
                    <span>Output / mapTo</span>
                    <input type="text" placeholder="For example: warranty_cost" [(ngModel)]="transformerMapToColumn" />
                  </label>
                  <label class="transformer-field" *ngIf="isRelationshipJoinOperation">
                    <span>Output Entity</span>
                    <select
                      [ngModel]="transformerOutputEntity"
                      (ngModelChange)="onTransformerOutputEntityChange($event)"
                      aria-label="Select output entity"
                    >
                      <option value="">
                        {{ transformerLookupEntitiesLoading ? 'Loading entities...' : 'Select output entity' }}
                      </option>
                      <option *ngFor="let entity of transformerOutputEntityOptions" [value]="entity">{{ entity }}</option>
                    </select>
                    <small class="transformer-field-error" *ngIf="transformerLookupEntitiesError">
                      {{ transformerLookupEntitiesError }}
                    </small>
                  </label>
                </section>

                <section class="transformer-section">
                  <h3>3. Select {{ isRelationshipJoinOperation ? 'Join' : 'Lookup' }} Columns</h3>
                  <div class="transformer-selection-box">
                    <div class="transformer-selection-heading">
                      <strong>{{ isRelationshipJoinOperation ? 'Join' : 'Lookup' }} Columns ({{ transformerSelectedColumns.length }})</strong>
                      <button type="button" (click)="transformerSelectedColumns = []">Clear All</button>
                    </div>
                    <div class="transformer-chips">
                      <span *ngFor="let columnName of transformerSelectedColumns">
                        {{ columnName }}
                        <button type="button" (click)="removeTransformerColumn(columnName)" [attr.aria-label]="'Remove ' + columnName"><mat-icon>close</mat-icon></button>
                      </span>
                    </div>
                    <select [attr.aria-label]="isRelationshipJoinOperation ? 'Add join column' : 'Add lookup column'" (change)="addTransformerColumn($event)">
                      <option value="">+ Add Column</option>
                      <option *ngFor="let columnName of transformerAvailableColumns" [value]="columnName" [disabled]="transformerSelectedColumns.includes(columnName)">{{ columnName }}</option>
                    </select>
                  </div>
                </section>

                <section class="transformer-section" *ngIf="isRelationshipJoinOperation">
                  <h3>4. Select Output Attribute</h3>
                  <label class="transformer-field">
                    <span>Output Attribute</span>
                    <select
                      [ngModel]="transformerOutputAttributeSelectValue"
                      (ngModelChange)="onTransformerOutputAttributeChange($event)"
                      [disabled]="!transformerOutputEntity"
                      aria-label="Select output attribute"
                    >
                      <option value="">Select output attribute</option>
                      <option [value]="transformerCreateOutputAttributeValue">+ Create new output attribute</option>
                      <option
                        *ngIf="transformerMapToColumn && !transformerCreatingOutputAttribute && !transformerOutputAttributeOptions.includes(transformerMapToColumn)"
                        [value]="transformerMapToColumn"
                      >
                        {{ transformerMapToColumn }}
                      </option>
                      <option *ngFor="let attribute of transformerOutputAttributeOptions" [value]="attribute">{{ attribute }}</option>
                    </select>
                  </label>
                  <label class="transformer-field" *ngIf="transformerCreatingOutputAttribute">
                    <span>New Output Attribute</span>
                    <input type="text" placeholder="For example: joined_memory_gb" [(ngModel)]="transformerMapToColumn" />
                  </label>
                </section>

                <section class="transformer-section">
                  <h3>{{ isRelationshipJoinOperation ? '5' : '4' }}. Operation</h3>
                  <label class="transformer-field">
                    <span>Operation Type</span>
                    <select
                      [ngModel]="transformerRuleType"
                      (ngModelChange)="selectTransformerRule($event)"
                      aria-label="Select relationship operation type"
                    >
                      <option *ngFor="let operationType of relationshipOperationTypes" [value]="operationType.id">
                        {{ operationType.label }}
                      </option>
                    </select>
                  </label>
                </section>

                <section class="transformer-section" *ngIf="!isRelationshipJoinOperation">
                  <h3>5. Lookup Options</h3>
                  <div class="transformer-option-grid">
                    <label class="transformer-field">
                      <span>Lookup Entity</span>
                      <ng-select
                        class="transformer-multi-select"
                        [items]="transformerLookupEntities"
                        [multiple]="true"
                        [closeOnSelect]="false"
                        [searchable]="true"
                        appendTo="body"
                        [ngModel]="transformerLookupEntitySelections"
                        (ngModelChange)="onTransformerLookupEntitiesChange($event)"
                        (open)="loadTransformerLookupEntities(true, false)"
                        [loading]="transformerLookupEntitiesLoading"
                        dropdownPanelClass="transformer-dropdown-panel"
                        placeholder="Select lookup entity"
                        aria-label="Select lookup entity"
                      >
                      </ng-select>
                      <small class="transformer-field-error" *ngIf="transformerLookupEntitiesError">
                        {{ transformerLookupEntitiesError }}
                      </small>
                    </label>
                    <label class="transformer-field">
                      <span>Lookup Result Field</span>
                      <input
                        type="text"
                        placeholder="For example: warrantyCost"
                        [(ngModel)]="transformerLookupResultField"
                      />
                    </label>
                    <label class="transformer-field">
                      <span>Lookup Keys</span>
                      <ng-select
                        class="transformer-multi-select"
                        [items]="transformerLookupResultFields"
                        [multiple]="true"
                        [closeOnSelect]="false"
                        [searchable]="true"
                        [(ngModel)]="transformerLookupKeys"
                        [disabled]="!transformerLookupEntity"
                        (open)="loadTransformerLookupEntities(true, false)"
                        aria-label="Select lookup keys"
                      >
                      </ng-select>
                      <small>Select one or more fields from the lookup entity.</small>
                    </label>
                    <label class="transformer-field">
                      <span>Default Value</span>
                      <input type="text" placeholder="0.00" [(ngModel)]="transformerDefaultValue" />
                    </label>
                  </div>
                </section>

                <section class="transformer-section transformer-metadata-preview" *ngIf="transformerOperationPreview as relationshipPreview">
                  <h3>Target Metadata Preview</h3>
                  <div class="transformer-metadata-grid">
                    <span>Attributes</span>
                    <strong>{{ transformerSelectedColumns.join(', ') }}</strong>
                    <span>Operation</span>
                    <strong>{{ relationshipPreview['operation'] }}</strong>
                    <span>Map To</span>
                    <strong>{{ transformerMapToPreview }}</strong>
                  </div>
                </section>

                <p class="transformer-status" *ngIf="transformerApplyMessage">{{ transformerApplyMessage }}</p>
                <div class="transformer-actions is-two-actions">
                  <button type="button" class="transformer-reset" (click)="resetTransformerOperations()">Reset</button>
                  <button type="button" class="transformer-apply" (click)="finalizeTransformerChanges()">Finalize Changes</button>
                </div>
              </div>
            </section>

            <section *ngSwitchCase="'validation'" class="transformer-operations">
              <button type="button" class="transformer-title" (click)="transformerExpanded = !transformerExpanded">
                <strong>Validation Operations</strong>
                <mat-icon>{{ transformerExpanded ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>

              <div class="transformer-body" *ngIf="transformerExpanded">
                <section class="transformer-section">
                  <h3>1. Select Files / Sources</h3>
                  <div class="transformer-picker">
                    <div class="transformer-chips">
                      <span *ngFor="let fileName of transformerSelectedFiles">
                        {{ fileName }}
                        <button type="button" (click)="removeTransformerFile(fileName)" [attr.aria-label]="'Remove ' + fileName"><mat-icon>close</mat-icon></button>
                      </span>
                    </div>
                    <select class="transformer-add-file" aria-label="Add validation source" (change)="addTransformerFile($event)">
                      <option value="">+ Add saved dataset...</option>
                      <option
                        *ngFor="let fileName of transformerAvailableFiles"
                        [value]="fileName"
                        [disabled]="transformerSelectedFiles.includes(fileName)"
                      >
                        {{ fileName }}
                      </option>
                    </select>
                  </div>
                </section>

                <section class="transformer-section">
                  <h3>2. Select Map To Column</h3>
                  <div class="transformer-radio-row">
                    <label><input type="radio" name="validation-output-mode" value="overwrite" [(ngModel)]="transformerOutputMode" /> Existing Column</label>
                    <label><input type="radio" name="validation-output-mode" value="create" [(ngModel)]="transformerOutputMode" /> New Column</label>
                  </div>
                  <label class="transformer-field" *ngIf="transformerOutputMode === 'overwrite'; else newValidationMapTo">
                    <span>Map To</span>
                    <select [(ngModel)]="transformerMapToColumn" aria-label="Select validation map to column">
                      <option value="">Select mapTo column</option>
                      <option *ngFor="let columnName of transformerAvailableColumns" [value]="columnName">{{ columnName }}</option>
                    </select>
                  </label>
                  <ng-template #newValidationMapTo>
                    <label class="transformer-field">
                      <span>New mapTo Column</span>
                      <input type="text" placeholder="For example: total_units" [(ngModel)]="transformerMapToColumn" />
                    </label>
                  </ng-template>
                </section>

                <section class="transformer-section">
                  <h3>3. Select Operation Columns</h3>
                  <div class="transformer-selection-box">
                    <div class="transformer-selection-heading">
                      <strong>Operation Columns ({{ transformerSelectedColumns.length }})</strong>
                      <button type="button" (click)="transformerSelectedColumns = []">Clear All</button>
                    </div>
                    <div class="transformer-chips">
                      <span *ngFor="let columnName of transformerSelectedColumns">
                        {{ columnName }}
                        <button type="button" (click)="removeTransformerColumn(columnName)" [attr.aria-label]="'Remove ' + columnName"><mat-icon>close</mat-icon></button>
                      </span>
                    </div>
                    <select aria-label="Add validation column" (change)="addTransformerColumn($event)">
                      <option value="">+ Add Column</option>
                      <option *ngFor="let columnName of transformerAvailableColumns" [value]="columnName" [disabled]="transformerSelectedColumns.includes(columnName)">{{ columnName }}</option>
                    </select>
                  </div>
                </section>

                <section class="transformer-section">
                  <h3>4. Select Operation</h3>
                  <label class="transformer-search">
                    <mat-icon>calculate</mat-icon>
                    <select
                      aria-label="Select validation operation"
                      [ngModel]="transformerRuleType"
                      (ngModelChange)="selectTransformerRule($event)"
                    >
                      <option *ngFor="let rule of validationRuleTypes" [value]="rule.id">{{ rule.label }}</option>
                    </select>
                  </label>
                </section>

                <section class="transformer-section">
                  <h3>5. Operation Options</h3>
                  <label class="transformer-field">
                    <span>Fixed Numeric Values (comma separated, optional)</span>
                    <input type="text" placeholder="For example: 5.00, 0.18" [(ngModel)]="transformerLiteralValues" />
                  </label>
                </section>

                <section class="transformer-section transformer-metadata-preview" *ngIf="transformerOperationPreview as validationPreview">
                  <h3>Target Metadata Preview</h3>
                  <div class="transformer-metadata-grid">
                    <span>Attributes</span>
                    <strong>{{ transformerSelectedColumns.join(', ') }}</strong>
                    <span>Operation</span>
                    <strong>{{ validationPreview['operation'] }}</strong>
                    <span>Map To</span>
                    <strong>{{ transformerMapToPreview }}</strong>
                  </div>
                </section>

                <p class="transformer-status" *ngIf="transformerApplyMessage">{{ transformerApplyMessage }}</p>
                <div class="transformer-actions is-two-actions">
                  <button type="button" class="transformer-reset" (click)="resetTransformerOperations()">Reset</button>
                  <button type="button" class="transformer-apply" (click)="finalizeTransformerChanges()">Finalize Changes</button>
                </div>
              </div>
            </section>

            <div *ngSwitchDefault class="pipeline-generic-details">
              <div class="pipeline-detail-node">
                <span [style.background]="selectedNode.accent + '18'" [style.color]="selectedNode.accent">
                  <mat-icon>{{ pipelineNodeIcon(selectedNode.type) }}</mat-icon>
                </span>
                <div>
                  <strong>{{ selectedNode.label }}</strong>
                  <small>{{ selectedNode.meta }}</small>
                </div>
              </div>
              <label>Node Name</label>
              <input type="text" [value]="selectedNode.label" />
              <label>Summary</label>
              <input type="text" [value]="selectedNode.summary || selectedNode.meta" />
            </div>
          </ng-container>
        </ng-container>

        <section class="pipeline-applied-steps" *ngIf="activePipelineDetailsTab === 'applied-steps'">
          <div class="pipeline-applied-empty" *ngIf="visiblePipelineAppliedSteps.length === 0">
            <mat-icon>history</mat-icon>
            <strong>No applied steps yet</strong>
            <span>Applied operations for the selected node and datasets will appear here.</span>
          </div>
          <article class="pipeline-applied-step" *ngFor="let step of visiblePipelineAppliedSteps; let index = index">
            <div class="pipeline-applied-step-icon" [class]="'is-' + step.type">
              <mat-icon>{{ pipelineNodeIcon(step.type) }}</mat-icon>
            </div>
            <div class="pipeline-applied-step-copy">
              <span class="pipeline-applied-sequence">Step {{ visiblePipelineAppliedSteps.length - index }}</span>
              <strong>{{ step.label }}</strong>
              <p>{{ step.summary }}</p>
              <span class="pipeline-applied-revision" *ngIf="!step.active">Superseded by a later edit</span>
              <small>{{ pipelineAppliedDatasetNames(step) }}</small>
              <time>{{ step.appliedAt | date:'MMM d, y, h:mm:ss a' }}</time>
            </div>
            <button
              type="button"
              class="pipeline-applied-edit"
              [disabled]="!step.active"
              (click)="editPipelineAppliedStep(step)"
            >
              <mat-icon>edit</mat-icon>
              {{ step.active ? 'Edit' : 'Revised' }}
            </button>
          </article>
        </section>
      </aside>
    </div>

    <div
      class="pipeline-source-modal-backdrop"
      *ngIf="pipelineTranslationPreviewOpen"
      (click)="closePipelineTranslationPreview()"
    >
      <section class="pipeline-translation-preview-modal" role="dialog" aria-modal="true" aria-label="Translation preview" (click)="$event.stopPropagation()">
        <button type="button" class="pipeline-source-modal-close" (click)="closePipelineTranslationPreview()" aria-label="Close">
          <mat-icon>close</mat-icon>
        </button>
        <div class="pipeline-translation-preview-header"></div>

        <div class="pipeline-translation-preview-body">
          <div class="message error-message" *ngIf="pipelineTranslationPreviewError">{{ pipelineTranslationPreviewError }}</div>
          <app-rule-builder
            class="pipeline-translation-rule-builder"
            *ngIf="!pipelineTranslationPreviewError"
            [initialFiles]="pipelineTranslationFiles"
            [initialDeamResponse]="pipelineWorkflowDeamResponse"
            [autoAnalyze]="true"
            (translationPreview)="applyPipelineTranslationPreview($event)"
            (translationAction)="completePipelineTranslationAction($event)"
          ></app-rule-builder>
        </div>
      </section>
    </div>
  </section>
</ng-container>

<ng-template #legacyDataBuilder>
  <section class="workspace stepper-workspace">
    <!-- ████ Stepper driven by the JSON layout config (see JSON_CONFIG_PLAN.md);
         each step body is a named <ng-template> resolved by its field id. The
         default layout reproduces the original 2-step order exactly. ████ -->
    <!-- ████ Config-driven stepper. Step ORDER/LABELS/VISIBILITY come from the
         JSON layout (see JSON_CONFIG_PLAN.md); each step's body stays inline as
         direct <mat-step> content (Material projects it eagerly — the reliable
         path). `stepLabel(id)` / `isStepVisible(id)` read the loaded layout. ████ -->
    <mat-stepper
      class="chart-stepper data-stepper"
      [orientation]="layout.stepperOrientation || 'horizontal'"
      [linear]="!!layout.linear"
      #dataStepper
    >

    <!-- ============== Upload data ============== -->
    @if (isStepVisible('upload')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('upload') }}</ng-template>
        <div class="step-content data-ingest-step">
          <div class="step-grid data-ingest-grid">
            <div class="step-form data-ingest-form">
              <div class="control-group upload-ingest-card">
                <label>
                  Upload data file (CSV / Excel)
                  <span class="label-info" matTooltip="Pick a .csv or .xlsx file. The first row should be column headers.">ⓘ</span>
                </label>
                <!-- Hidden native file input — triggered by buttons below -->
                <input
                  #fileInput
                  id="dataFile"
                  type="file"
                  multiple
                  [attr.accept]="acceptedFileTypes"
                  (change)="onFileSelected($event)"
                  style="display:none"
                />

                <div class="file-upload-area">
                  <div class="pipeline-source-loading" *ngIf="fileUploadLoading">
                    <span class="pipeline-spinner" aria-hidden="true"></span>
                    <span>{{ fileUploadStatusText }}</span>
                  </div>

                  <!-- No files yet: show Browse button -->
                  <button *ngIf="!fileUploadLoading && !uploadedFiles.length" type="button" class="browse-btn" (click)="fileInput.click()">
                    <mat-icon>upload_file</mat-icon>
                    Browse files
                  </button>

                  <!-- Files uploaded: chips + add-more button -->
                  <div *ngIf="uploadedFiles.length" class="file-chips-row">
                    <div class="file-chip" *ngFor="let f of uploadedFiles; let i = index">
                      <mat-icon class="chip-file-icon">description</mat-icon>
                      <span class="chip-name">{{ f.name }}</span>
                      <button type="button" class="chip-remove" (click)="removeUploadedFile(i)" matTooltip="Remove file">
                        <mat-icon>close</mat-icon>
                      </button>
                    </div>
                    <button type="button" class="add-more-btn" (click)="fileInput.click()" matTooltip="Add more files">
                      <mat-icon>add</mat-icon>
                    </button>
                  </div>
                </div>

                <!-- <small class="control-hint">Supported formats: CSV, Excel (.xlsx, .xls)</small> -->
              </div>

              <ng-container *ngIf="workbookFile && !workbookFromPipeline">
                <ng-container *ngTemplateOutlet="workbookPanel"></ng-container>
              </ng-container>

              <div class="control-group entity-ingest-card">
                <label for="entitySelect">
                  Select File
                  <span class="label-info" matTooltip="Loads entity names from the backend entity API."></span>
                </label>
                <ng-select
                  labelForId="entitySelect"
                  #ssSel
                  class="ss-select"
                  [class.is-open]="!!ssSel.isOpen?.()"
                  appendTo="body"
                  ngClass="ss-dropdown-panel"
                  [items]="entityOptions"
                  [clearable]="true"
                  [searchable]="true"
                  [loading]="entitiesLoading"
                  placeholder="Select"
                  [(ngModel)]="selectedEntity"
                  (ngModelChange)="onEntitySelectionChange($event)"
                ></ng-select>
                <small class="control-warning" *ngIf="entitiesErrorMessage">{{ entitiesErrorMessage }}</small>
                <small class="control-warning" *ngIf="entityDataErrorMessage">{{ entityDataErrorMessage }}</small>
                <div class="entity-preview-actions" *ngIf="selectedEntity">
                  <div class="entity-preview-meta" *ngIf="entityTableRows.length && entityTableColumns.length">
                    <span>{{ entityTableRows.length }} rows</span>
                    <span>{{ entityTableColumns.length }} visible columns</span>
                  </div>
                  <!-- <button
                    type="button"
                    class="secondary-action entity-preview-button"
                    [disabled]="entityDataLoading || !entityTableRows.length || !entityTableColumns.length"
                    (click)="openEntityPreview()"
                  >
                    See Preview
                  </button> -->
                </div>
                <small class="control-hint" *ngIf="selectedEntity && entityDataLoading">Preparing entity preview...</small>
                <small
                  class="control-hint"
                  *ngIf="selectedEntity && !entityDataLoading && !entityDataErrorMessage && !entityTableRows.length"
                >
                  No rows returned for the selected entity.
                </small>
              </div>

              <!-- <div class="reset-row" *ngIf="dataset || uploadedFiles.length">
                <button
                  type="button"
                  class="reset-action"
                  (click)="startNewChart()"
                >
                  Reset
                </button>
              </div> -->

              <div *ngIf="validationMessage" class="message warning-message">{{ validationMessage }}</div>

            </div>

          </div>

          <div *ngIf="showDataPreview" class="data-preview-card">
            <div class="data-preview-header">
              <div class="preview-title-block">
                <span class="preview-eyebrow"><mat-icon>table_view</mat-icon> Dataset explorer</span>
                <h3>Data Preview</h3>
                <span class="preview-summary">
                  Showing {{ previewRangeStart | number }}–{{ previewRangeEnd | number }} of {{ previewTotalRows | number }} rows
                  <span class="preview-summary-dot">•</span>
                  {{ dataset?.columns?.length || 0 }} columns
                </span>
              </div>
              <small class="control-hint" *ngIf="canEditPreviewData">
                Click a cell to edit. Arrow keys/Tab/Enter to move, drag to select a range, copy-paste from Excel, drag the corner handle to fill down.
              </small>

              <span class="preview-download-actions" *ngIf="previewTotalRows">
                <mat-icon class="download-icon">download</mat-icon>
                <span class="download-label">Download all {{ previewTotalRows | number }} rows</span>
                <button
                  type="button"
                  class="download-format-button"
                  (click)="downloadPreviewData('csv')"
                  [disabled]="downloadingPreview"
                  matTooltip="Download the full dataset as a CSV file"
                >CSV</button>
                <button
                  type="button"
                  class="download-format-button"
                  (click)="downloadPreviewData('xlsx')"
                  [disabled]="downloadingPreview"
                  matTooltip="Download the full dataset as an Excel workbook"
                >Excel</button>
              </span>
            </div>

            <div class="grid-toolbar" *ngIf="canEditPreviewData">
              <button type="button" class="grid-tool-btn" (click)="undo()" [disabled]="!canUndo" matTooltip="Undo (Ctrl+Z)">
                <mat-icon>undo</mat-icon>
              </button>
              <button type="button" class="grid-tool-btn" (click)="redo()" [disabled]="!canRedo" matTooltip="Redo (Ctrl+Y)">
                <mat-icon>redo</mat-icon>
              </button>
              <span class="grid-toolbar-divider"></span>
              <button
                type="button"
                class="grid-tool-btn grid-tool-btn-labeled"
                [class.is-active]="showFindReplace"
                (click)="toggleFindReplace()"
              >
                <mat-icon>find_replace</mat-icon>
                Find &amp; Replace
              </button>
              <span class="row-action-message" *ngIf="rowActionMessage">{{ rowActionMessage }}</span>
            </div>

            <div class="find-replace-bar" *ngIf="canEditPreviewData && showFindReplace">
              <select [(ngModel)]="findReplaceColumn" [ngModelOptions]="{ standalone: true }">
                <option value="" disabled selected>Column…</option>
                <option *ngFor="let column of dataset?.columns || []" [value]="column.name" [disabled]="!isEditableColumn(column.name)">
                  {{ column.name }}
                </option>
              </select>
              <input type="text" placeholder="Find this exact value" [(ngModel)]="findReplaceFind" [ngModelOptions]="{ standalone: true }" />
              <input type="text" placeholder="Replace with" [(ngModel)]="findReplaceReplace" [ngModelOptions]="{ standalone: true }" />
              <button type="button" class="primary-action" (click)="runFindReplace()">Replace All</button>
              <small class="find-replace-message" *ngIf="findReplaceMessage">{{ findReplaceMessage }}</small>
            </div>

            <div class="cell-edit-save-bar" *ngIf="canEditPreviewData && (hasPendingCellEdits || cellEditStatusMessage)">
              <span class="cell-edit-count" *ngIf="hasPendingCellEdits">
                {{ pendingCellEditCount }} unsaved change{{ pendingCellEditCount === 1 ? '' : 's' }}
              </span>
              <span
                class="cell-edit-status"
                [class.success]="cellEditStatus === 'success'"
                [class.error]="cellEditStatus === 'error'"
                *ngIf="cellEditStatusMessage"
              >{{ cellEditStatusMessage }}</span>
              <span class="cell-edit-actions" *ngIf="hasPendingCellEdits">
                <button type="button" class="secondary-action" (click)="discardPreviewEdits()" [disabled]="savingCellEdits">
                  Discard
                </button>
                <button type="button" class="primary-action" (click)="savePreviewEdits()" [disabled]="savingCellEdits">
                  {{ savingCellEdits ? 'Saving...' : 'Save to Database' }}
                </button>
              </span>
            </div>

            <div class="table-wrap">
              <table class="preview-grid" (keydown)="onGridKeydown($event)" (copy)="onGridCopy($event)" (paste)="onGridPaste($event)">
                <colgroup>
                  <col
                    *ngFor="let column of dataset?.columns || []"
                    [style.width.px]="getColumnWidth(column.name)"
                  />
                </colgroup>
                <thead>
                  <tr>
                    <th *ngFor="let column of dataset?.columns || []; let colIndex = index" class="preview-column-header">
                      <span class="column-heading">
                        <span class="column-name" [matTooltip]="column.name">{{ formatColumnLabel(column.name) }}</span>
                      </span>
                      <span
                        class="column-resize-handle"
                        [class.is-resizing]="resizingColumnName === column.name"
                        role="separator"
                        tabindex="0"
                        aria-orientation="vertical"
                        [attr.aria-label]="'Resize ' + column.name + ' column'"
                        [attr.aria-valuenow]="getColumnWidth(column.name)"
                        (mousedown)="startColumnResize($event, column.name)"
                        (dblclick)="autoFitColumn($event, column.name)"
                        (keydown)="onColumnResizeKeydown($event, column.name)"
                        matTooltip="Drag to resize · Double-click to auto-fit"
                      ></span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let row of previewRows; let i = index">
                    <td
                      *ngFor="let column of dataset?.columns || []; let colIndex = index"
                      [class.grid-cell-active]="isCellActive(absoluteRowIndex(i), colIndex)"
                      [class.grid-cell-selected]="isCellInSelection(absoluteRowIndex(i), colIndex)"
                      (mousedown)="onCellMouseDown($event, absoluteRowIndex(i), colIndex)"
                      (mouseenter)="onCellMouseEnter(absoluteRowIndex(i), colIndex)"
                    >
                      <ng-container *ngIf="canEditPreviewData && isEditableColumn(column.name); else readonlyCell">
                        <ng-select
                          *ngIf="getColumnEditorKind(column.name) === 'select'; else textOrNumberEditor"
                          [id]="'grid-cell-' + absoluteRowIndex(i) + '-' + colIndex"
                          [attr.data-row]="absoluteRowIndex(i)"
                          [attr.data-col]="colIndex"
                          class="cell-edit-input cell-edit-select cell-edit-rounded-select"
                          [items]="getColumnEditorOptions(column.name)"
                          [clearable]="false"
                          [searchable]="false"
                          appendTo="body"
                          [ngModel]="row[column.name]"
                          (open)="onPreviewDropdownOpen(absoluteRowIndex(i), colIndex, column.name, row[column.name])"
                          (close)="onPreviewDropdownClose(absoluteRowIndex(i), colIndex, column.name)"
                          (ngModelChange)="onPreviewDropdownChange(absoluteRowIndex(i), colIndex, column.name, $event)"
                          (focus)="onCellFocus(absoluteRowIndex(i), colIndex)"
                          (blur)="onCellBlur()"
                          [ngModelOptions]="{ standalone: true }"
                          [disabled]="savingCellEdits"
                        ></ng-select>
                        <ng-template #textOrNumberEditor>
                          <input
                            [id]="'grid-cell-' + absoluteRowIndex(i) + '-' + colIndex"
                            [attr.data-row]="absoluteRowIndex(i)"
                            [attr.data-col]="colIndex"
                            type="text"
                            [inputMode]="getColumnEditorKind(column.name) === 'number' ? 'decimal' : 'text'"
                            class="cell-edit-input"
                            [ngModel]="row[column.name]"
                            (ngModelChange)="onPreviewCellEdit(absoluteRowIndex(i), column.name, $event)"
                            (focus)="onCellFocus(absoluteRowIndex(i), colIndex)"
                            (blur)="onCellBlur()"
                            [ngModelOptions]="{ standalone: true }"
                            [disabled]="savingCellEdits"
                          />
                        </ng-template>
                        <span
                          class="grid-fill-handle"
                          *ngIf="isFillHandleCell(absoluteRowIndex(i), colIndex)"
                          (mousedown)="onFillHandleMouseDown($event)"
                        ></span>
                      </ng-container>
                      <ng-template #readonlyCell>{{ row[column.name] }}</ng-template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="preview-grid-footer-note">
              <span><mat-icon>swap_horiz</mat-icon> Drag a column edge to resize</span>
              <span><mat-icon>ads_click</mat-icon> Double-click an edge to auto-fit</span>
            </div>

            <div class="preview-pagination" *ngIf="previewTotalRows > previewPageSizeOptions[0]">
              <label class="preview-page-size">
                Rows per page
                <select
                  [ngModel]="previewPageSize"
                  (ngModelChange)="onPreviewPageSizeChange($event)"
                  [ngModelOptions]="{ standalone: true }"
                >
                  <option *ngFor="let size of previewPageSizeOptions" [ngValue]="size">{{ size }}</option>
                </select>
              </label>

              <span class="preview-page-info">
                Page {{ previewPage + 1 }} of {{ previewTotalPages | number }}
              </span>

              <span class="preview-page-buttons">
                <button type="button" (click)="goToPreviewPage(0)" [disabled]="previewPage === 0" matTooltip="First page">«</button>
                <button type="button" (click)="goToPreviewPage(previewPage - 1)" [disabled]="previewPage === 0" matTooltip="Previous page">‹</button>
                <button type="button" (click)="goToPreviewPage(previewPage + 1)" [disabled]="previewPage >= previewTotalPages - 1" matTooltip="Next page">›</button>
                <button type="button" (click)="goToPreviewPage(previewTotalPages - 1)" [disabled]="previewPage >= previewTotalPages - 1" matTooltip="Last page">»</button>
              </span>
            </div>
          </div>

          <div class="step-nav">
            <span></span>
            <button mat-flat-button color="primary" matStepperNext [disabled]="!hasDataset">Next: Computations →</button>
          </div>
        </div>
      </mat-step>
    }

    <!-- ============== Computation builder ============== -->
    @if (isStepVisible('computations')) {
      <mat-step>
        <ng-template matStepLabel>{{ stepLabel('computations') }}</ng-template>
        <div class="step-content compact-computation-step" *ngIf="dataset; else computationNeedsData">

          <ng-container *ngIf="isEntityDatasetMode; else legacyComputationBuilder">
            <div class="formula-panel entity-computation-panel">
              <div class="formula-panel-header">
                <h2>Dynamic Formula Builder</h2>
              </div>
              <p class="formula-hint-block">
                Type manual formula expressions, add aliases, define row filters, and execute them through the backend query.
              </p>

              <div class="formula-template-bar">
                <small class="control-hint">Available columns. Click a column to insert it into the formula editor.</small>
                <div class="formula-template-chips">
                  <button
                    type="button"
                    *ngFor="let column of entityAvailableColumns"
                    class="formula-template-chip chip-column"
                    (click)="insertEntityColumn(column.name)"
                  >
                    {{ column.name }}
                  </button>
                </div>
              </div>

              <div class="entity-computation-grid">
                <section class="entity-computation-section entity-formula-section">
                  <div class="formula-section-heading">
                    <h3 class="formula-section">Formulas</h3>
                    <button
                      type="button"
                      class="functions-help-toggle"
                      [class.is-open]="showFunctionHelp"
                      (click)="toggleFunctionHelp()"
                      matTooltip="Show the full list of supported functions"
                    >
                      <mat-icon>functions</mat-icon>
                      Functions reference
                      <mat-icon class="chevron">expand_more</mat-icon>
                    </button>
                  </div>
                  <small class="control-hint entity-formula-operators">
                    Operators <code>+</code> <code>-</code> <code>*</code> <code>/</code> <code>( )</code> and constants like <code>100</code>, <code>0.25</code> are always allowed.
                  </small>

                  <div class="functions-help" *ngIf="showFunctionHelp">
                    <div class="functions-ref-grid">
                      <div class="functions-ref-row" *ngFor="let group of functionReference">
                        <span class="functions-ref-label">{{ group.label }}</span>
                        <code
                          *ngFor="let snippet of group.snippets"
                          class="functions-ref-code"
                          role="button"
                          tabindex="0"
                          matTooltip="Click to insert, then click a column to fill each parameter"
                          (click)="insertFunctionSnippet(snippet)"
                          (keydown.enter)="insertFunctionSnippet(snippet)"
                          (keydown.space)="$event.preventDefault(); insertFunctionSnippet(snippet)"
                        >{{ snippet }}</code>
                      </div>
                    </div>
                    <small class="control-hint functions-ref-tip">Tip: click a function to insert it — its first parameter is highlighted, then click an available column above to fill each parameter in turn. Column names with spaces are wrapped as <code>[Revenue]</code> automatically.</small>
                  </div>
                  <div class="formula-add entity-formula-name">
                    <input type="text" [(ngModel)]="entityFormulaName" placeholder="Formula name / alias (e.g., gross_margin)" />
                  </div>
                  <div class="formula-add entity-formula-expression">
                    <textarea
                      #entityFormulaEditor
                      [(ngModel)]="entityFormulaExpression"
                      rows="3"
                      placeholder="Type formula expression manually (e.g., ((list_price_usd - price_usd) / list_price_usd) * 100)"
                    ></textarea>
                  </div>
                  <div class="formula-add entity-action-row">
                    <button type="button" class="secondary-action formula-add-btn" (click)="addEntityFormula()">
                      {{ editingEntityFormulaIndex === null ? 'Add Formula' : 'Update Formula' }}
                    </button>
                    <button type="button" class="formula-remove" *ngIf="editingEntityFormulaIndex !== null" (click)="cancelEntityFormulaEdit()">Cancel</button>
                  </div>
                  <small class="control-warning" *ngIf="entityFormulaError">{{ entityFormulaError }}</small>

                  <div class="formula-existing formula-grid formula-grid-formulas" *ngIf="entityFormulas.length">
                    <div class="formula-grid-header">
                      <span>Formula Name</span>
                      <span>Expression</span>
                      <span>Actions</span>
                    </div>
                    <div class="formula-item formula-grid-row" *ngFor="let formula of entityFormulas; let i = index">
                      <div class="formula-info formula-grid-name">
                        <strong>{{ formula.name }}</strong>
                      </div>
                      <div class="formula-info formula-grid-expression">
                        <small>{{ formula.expression }}</small>
                        <small>{{ formula.expression }} as {{ formula.name }}</small>
                      </div>
                      <div class="formula-item-actions formula-grid-actions">
                        <button type="button" class="formula-edit" (click)="editEntityFormula(i)">Edit</button>
                        <button type="button" class="formula-remove" (click)="deleteEntityFormula(i)">Delete</button>
                      </div>
                    </div>
                  </div>
                </section>

                <section class="entity-computation-section entity-filter-section">
                  <h3 class="formula-section">Row Filters</h3>
                  <div class="entity-filter-grid">
                    <div class="formula-add">
                      <ng-select
                        #ssSel
                        class="ss-select"
                        [class.is-open]="!!ssSel.isOpen?.()"
                        appendTo="body"
                        ngClass="ss-dropdown-panel"
                        [items]="dataset?.columns || []"
                        bindLabel="name"
                        bindValue="name"
                        [clearable]="true"
                        [searchable]="true"
                        placeholder="Select column"
                        [(ngModel)]="entityFilterColumn"
                      ></ng-select>
                    </div>
                    <div class="formula-add">
                      <ng-select
                        #ssSel
                        class="ss-select"
                        [class.is-open]="!!ssSel.isOpen?.()"
                        appendTo="body"
                        ngClass="ss-dropdown-panel"
                        [items]="entityFilterOperators"
                        [clearable]="false"
                        [searchable]="false"
                        placeholder="Operator"
                        [(ngModel)]="entityFilterOperator"
                        (ngModelChange)="onEntityFilterOperatorChange($event)"
                      ></ng-select>
                    </div>
                    <div class="formula-add">
                      <input
                        type="text"
                        [(ngModel)]="entityFilterValue"
                        [disabled]="!entityFilterRequiresValue"
                        [placeholder]="entityFilterValuePlaceholder"
                      />
                    </div>
                    <div class="formula-add">
                      <ng-select
                        #ssSel
                        class="ss-select"
                        [class.is-open]="!!ssSel.isOpen?.()"
                        appendTo="body"
                        ngClass="ss-dropdown-panel"
                        [items]="entityLogicalOperators"
                        [clearable]="false"
                        [searchable]="false"
                        placeholder="Logical operator"
                        [(ngModel)]="entityFilterLogicalOperator"
                      ></ng-select>
                    </div>
                  </div>
                  <div class="formula-add entity-action-row">
                    <button type="button" class="secondary-action formula-add-btn" (click)="addEntityFilter()">
                      {{ editingEntityFilterIndex === null ? 'Add Filter' : 'Update Filter' }}
                    </button>
                    <button type="button" class="formula-remove" *ngIf="editingEntityFilterIndex !== null" (click)="cancelEntityFilterEdit()">Cancel</button>
                  </div>
                  <small class="control-warning" *ngIf="entityFilterError">{{ entityFilterError }}</small>

                  <div class="formula-existing entity-filter-list" *ngIf="entityFilters.length">
                    <div class="formula-item" *ngFor="let filter of entityFilters; let i = index">
                      <div class="formula-info">
                        <strong>{{ filter.attributeName }} {{ filter.operator }}</strong>
                        <small>{{ (filter.attributeValue ?? []).join(', ') || '(no value)' }} <span *ngIf="i < entityFilters.length - 1">· {{ filter.logicalOperator }}</span></small>
                      </div>
                      <div class="formula-item-actions">
                        <button type="button" class="formula-edit" (click)="editEntityFilter(i)">Edit</button>
                        <button type="button" class="formula-remove" (click)="deleteEntityFilter(i)">Delete</button>
                      </div>
                    </div>
                  </div>
                </section>
              </div>

              <!-- ████ Builder-wide actions. These operate on ALL formulas AND
                   row filters together, so they live below the two-column grid
                   rather than inside the Row Filters section. ████ -->
              <div class="entity-builder-actions">
                <div class="entity-action-buttongroup">
                  <!-- Preview (client-side compute) and Save Computed Values hidden per request.
                  <button
                    type="button"
                    class="pill-btn pill-btn-ghost"
                    (click)="computeEntityPreview()"
                    [disabled]="entityDataLoading || entityExecuting"
                    matTooltip="Compute formulas in the browser and preview results without saving"
                  >
                    <mat-icon>visibility</mat-icon>
                    Preview
                  </button>
                  <button
                    type="button"
                    class="pill-btn pill-btn-outline"
                    (click)="saveComputedPreview()"
                    [disabled]="!computedPreviewAvailable || entityExecuting || entityDataLoading"
                    matTooltip="Write the previewed computed values to the database"
                  >
                    <mat-icon>save</mat-icon>
                    Save Computed Values
                  </button>
                  -->
                  <button
                    type="button"
                    class="pill-btn pill-btn-primary"
                    [class.is-loading]="entityExecuting"
                    [disabled]="entityExecuting || entityDataLoading"
                    (click)="executeEntityComputations()"
                    matTooltip="Run formulas and row filters through the backend query"
                  >
                    <mat-icon *ngIf="!entityExecuting">play_arrow</mat-icon>
                    {{ entityExecuting ? 'Executing…' : 'Execute' }}
                  </button>
                </div>
                <div
                  class="entity-execution-status"
                  *ngIf="entityExecuteStatus !== 'idle'"
                  [class.status-pending]="entityExecuteStatus === 'pending'"
                  [class.status-running]="entityExecuteStatus === 'running'"
                  [class.status-success]="entityExecuteStatus === 'success'"
                  [class.status-error]="entityExecuteStatus === 'error'"
                >
                  <strong>
                    {{
                      entityExecuteStatus === 'running' ? 'Executing query' :
                      entityExecuteStatus === 'success' ? 'Execution complete' :
                      entityExecuteStatus === 'error' ? 'Execution needs attention' :
                      'Ready to execute'
                    }}
                  </strong>
                  <span>
                    {{
                      entityExecuteStatus === 'error'
                        ? entityDataErrorMessage
                        : entityExecuteMessage
                    }}
                  </span>
                  <small>
                    {{ entityFormulas.length }} saved formula(s) · {{ entityFilters.length }} row filter(s)
                    <ng-container *ngIf="entityExecuteStatus === 'success'">
                      · {{ entityLastExecutedRows }} rows · {{ entityLastExecutedColumns }} columns
                    </ng-container>
                  </small>
                </div>
              </div>

            </div>
          </ng-container>

          <ng-template #legacyComputationBuilder>
          <div class="formula-panel">
            <div class="formula-panel-header">
              <h2>Computation builder</h2>
              <div class="formula-panel-actions">
                <button
                  type="button"
                  class="formula-remove library-toggle-btn"
                  [class.active]="showLibraryPanel"
                  (click)="toggleLibraryPanel()"
                >
                  {{ showLibraryPanel ? 'Hide' : 'Library' }}
                  <span class="formula-badge" *ngIf="computationLibrary.length">{{ computationLibrary.length }}</span>
                </button>
                <button
                  type="button"
                  class="formula-remove deam-sync-btn"
                  title="Send the current dataset, computed columns, and aggregations to the DEAM Upload API (backend wiring is Task 5)."
                  (click)="syncToDeam()"
                >
                  Sync to DEAM
                </button>
              </div>
            </div>
            <p class="formula-hint-block">
              Operators: + - * / and parentheses. Comparison: == != &lt; &lt;= &gt; &gt;=. Logical: AND, OR, NOT. Strings need quotes. Reference columns by name; for names with spaces use [Column Name]. Functions: IF(cond,then,else), concat, upper/lower/trim, year/month/day, abs/round/sqrt/min/max.
            </p>
            <small class="control-hint" *ngIf="deamSyncMessage">{{ deamSyncMessage }}</small>

            <div class="formula-template-bar">
              <small class="control-hint">Quick templates — click to fill the form below:</small>
              <div class="formula-template-chips">
                <button
                  type="button"
                  *ngFor="let tpl of formulaTemplates"
                  class="formula-template-chip"
                  [class.chip-column]="tpl.kind === 'column'"
                  [class.chip-aggregation]="tpl.kind === 'aggregation'"
                  [title]="tpl.description"
                  (click)="applyTemplate(tpl)"
                >
                  <span class="chip-category">{{ tpl.category }}</span>
                  {{ tpl.label }}
                </button>
              </div>
            </div>

            <div class="library-panel" *ngIf="showLibraryPanel">
              <div class="library-panel-header">
                <h3>Computation library</h3>
                <small>Reuse across datasets. Updating a column entry re-derives matching columns in saved charts.</small>
              </div>
              <div *ngIf="!computationLibrary.length" class="library-empty">
                No saved computations yet. Use the <strong>★</strong> button beside any column or aggregation to save it.
              </div>
              <div class="library-entries" *ngIf="computationLibrary.length">
                <div class="library-entry" *ngFor="let entry of computationLibrary">
                  <div class="library-entry-row">
                    <div class="library-entry-info">
                      <strong>
                        {{ entry.label || entry.name }}
                        <span class="library-kind-badge" [class.badge-column]="entry.kind === 'column'" [class.badge-aggregation]="entry.kind === 'aggregation'">
                          {{ entry.kind === 'column' ? 'Column' : 'Aggregation' }}
                        </span>
                        <span class="library-version-badge" *ngIf="entry.versions.length">v{{ entry.versions.length + 1 }}</span>
                      </strong>
                      <small>= {{ entry.expression }}</small>
                      <small class="library-meta">Updated {{ entry.updatedAt | date: 'short' }}</small>
                    </div>
                    <div class="library-entry-actions">
                      <button type="button" class="library-apply-btn" (click)="applyLibraryEntry(entry)">Apply</button>
                      <button type="button" class="library-versions-btn" *ngIf="entry.versions.length" (click)="toggleVersions(entry.id)">
                        Versions ({{ entry.versions.length }})
                      </button>
                      <button type="button" class="formula-remove" (click)="removeLibraryEntry(entry, $event)">Remove</button>
                    </div>
                  </div>
                  <div class="library-versions" *ngIf="viewingVersionsId === entry.id && entry.versions.length">
                    <div class="library-version" *ngFor="let v of entry.versions; let i = index">
                      <div class="library-version-info">
                        <small><strong>v{{ entry.versions.length - i }}</strong> · {{ v.savedAt | date: 'short' }}</small>
                        <small>= {{ v.expression }}</small>
                      </div>
                      <button type="button" class="library-restore-btn" (click)="restoreLibraryVersion(entry, i)">Restore</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h3 class="formula-section">Computed columns</h3>
            <div class="formula-existing" *ngIf="computedColumns.length">
              <ng-container *ngFor="let column of computedColumns">
                <div class="formula-item" *ngIf="editingComputedColumn !== column.name">
                  <div class="formula-info">
                    <strong>{{ column.name }}</strong>
                    <small>= {{ column.expression }}</small>
                  </div>
                  <div class="formula-item-actions">
                    <button
                      type="button"
                      class="library-save-btn"
                      title="Save / update in computation library"
                      (click)="saveColumnToLibrary(column)"
                    >★ Library</button>
                    <button type="button" class="formula-edit" (click)="beginEditComputedColumn(column)">Edit</button>
                    <button type="button" class="formula-remove" (click)="removeComputedColumn(column.name)">Remove</button>
                  </div>
                </div>
                <div class="formula-edit-form" *ngIf="editingComputedColumn === column.name">
                  <input type="text" [(ngModel)]="editingComputedColumnName" placeholder="Column name" />
                  <input type="text" [(ngModel)]="editingComputedColumnExpression" placeholder="Expression" />
                  <div class="formula-edit-actions">
                    <button type="button" class="secondary-action formula-save-btn" (click)="saveEditComputedColumn(column.name)">Save</button>
                    <button type="button" class="formula-remove" (click)="cancelEditComputedColumn()">Cancel</button>
                  </div>
                  <small class="control-warning" *ngIf="editingComputedColumnError">{{ editingComputedColumnError }}</small>
                </div>
              </ng-container>
            </div>
            <div class="formula-add">
              <input type="text" [(ngModel)]="newColumnName" placeholder="Column name (e.g., Margin)" />
              <input type="text" [(ngModel)]="newColumnExpression" placeholder="Expression (e.g., (Profit / Revenue) * 100)" />
              <button type="button" class="secondary-action formula-add-btn" (click)="addComputedColumn()">Add column</button>
            </div>
            <small class="control-warning" *ngIf="newColumnError">{{ newColumnError }}</small>

            <h3 class="formula-section">
              Row filter
              <span class="label-info" matTooltip="Only rows where the expression is true are charted. Leave blank for no filter.">ⓘ</span>
            </h3>
            <input
              id="rowFilterData"
              type="text"
              [(ngModel)]="rowFilter"
              (ngModelChange)="onRowFilterChange()"
              placeholder='e.g., Revenue > 50000 AND Region == "North"'
            />
            <small class="control-warning" *ngIf="rowFilterError">{{ rowFilterError }}</small>

            <h3 class="formula-section">Custom aggregations</h3>
            <div class="formula-existing" *ngIf="customAggregations.length">
              <ng-container *ngFor="let agg of customAggregations">
                <div class="formula-item" *ngIf="editingAggregation !== agg.key">
                  <div class="formula-info">
                    <strong>{{ agg.label }}</strong>
                    <small>= {{ agg.expression }}</small>
                  </div>
                  <div class="formula-item-actions">
                    <button
                      type="button"
                      class="library-save-btn"
                      title="Save / update in computation library"
                      (click)="saveAggregationToLibrary(agg)"
                    >★ Library</button>
                    <button type="button" class="formula-edit" (click)="beginEditAggregation(agg)">Edit</button>
                    <button type="button" class="formula-remove" (click)="removeCustomAggregation(agg.key)">Remove</button>
                  </div>
                </div>
                <div class="formula-edit-form" *ngIf="editingAggregation === agg.key">
                  <input type="text" [(ngModel)]="editingAggregationLabel" placeholder="Label" />
                  <input type="text" [(ngModel)]="editingAggregationExpression" placeholder="Expression" />
                  <div class="formula-edit-actions">
                    <button type="button" class="secondary-action formula-save-btn" (click)="saveEditAggregation(agg.key)">Save</button>
                    <button type="button" class="formula-remove" (click)="cancelEditAggregation()">Cancel</button>
                  </div>
                  <small class="control-warning" *ngIf="editingAggregationError">{{ editingAggregationError }}</small>
                </div>
              </ng-container>
            </div>
            <div class="formula-add">
              <input type="text" [(ngModel)]="newAggregationLabel" placeholder="Label (e.g., RevPerUnit)" />
              <input type="text" [(ngModel)]="newAggregationExpression" placeholder="Expression (e.g., sum(Revenue) / sum(Units))" />
              <button type="button" class="secondary-action formula-add-btn" (click)="addCustomAggregation()">Add aggregation</button>
            </div>
            <small class="control-hint">
              Inside aggregations use: sum, avg, count, distinctCount, min, max, median, percentile(col,p), first, last, range. Columns reference values within each group.
            </small>
            <small class="control-warning" *ngIf="newAggregationError">{{ newAggregationError }}</small>
          </div>

          </ng-template>

          <div class="step-nav">
            <button mat-stroked-button matStepperPrevious>← Back to Upload</button>
            <button mat-flat-button color="primary" (click)="setViewMode('charts')">Continue to Chart Builder →</button>
          </div>
        </div>
        <ng-template #computationNeedsData>
          <div class="empty-state step-empty">Upload a file in the previous step to add computations.</div>
        </ng-template>
      </mat-step>
    }

    </mat-stepper>
  </section>
</ng-template>
</ng-template>

<ng-template #workbookPanel>
  <div class="control-group workbook-ingest-card">
    <label>
      {{ workbookReport?.sheets?.length || 'Several' }} sheets detected
      <span class="label-info" matTooltip="Each sheet becomes its own entity and workflow. Save datasets ingests every sheet in dependency order.">ⓘ</span>
    </label>

    <div class="pipeline-source-loading" *ngIf="workbookBusy">
      <span class="pipeline-spinner" aria-hidden="true"></span>
      <span>{{ workbookStatusText }}</span>
    </div>

    <div class="workbook-error" *ngIf="workbookError">{{ workbookError }}</div>

    <ul class="workbook-sheet-list" *ngIf="workbookReport">
      <li class="workbook-sheet-head" *ngFor="let sheet of workbookReport.sheets; trackBy: trackWorkbookSheet"
          [matTooltip]="sheet.error || workbookRunStatus(sheet.workflowName)?.failureReason || ''">
        <mat-icon class="chip-file-icon">table_chart</mat-icon>
        <span>{{ sheet.sheetName }}</span>
        <span class="workbook-run bad" *ngIf="sheet.error">not created</span>
        <ng-container *ngIf="!sheet.error && workbookRunStatus(sheet.workflowName) as run">
          <span class="workbook-run" [class.ok]="workbookRunSucceeded(run)" [class.bad]="!workbookRunSucceeded(run)">
            {{ workbookRunSucceeded(run) ? 'ingested' : 'failed' }}<ng-container *ngIf="run.rowsIngested !== undefined"> · {{ run.rowsIngested }} rows</ng-container>
          </span>
        </ng-container>
      </li>
    </ul>
  </div>
</ng-template>
